import React from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import { lighten } from 'polished';
import Theme from '../common/NekoTheme';
import { Icon, IconifyIcon } from '@iconify/react';
import PresetIcons from '../common/PresetIcons';

const StyledButton = Styled(props => {

  const { className, icon = null, onClick, disabled = false, color = null, small, children, ...rest } = props;
  const hasIcon = !!icon;

  const getIcon = () => {
    if (typeof icon === 'string') {
      return PresetIcons[icon] || null;
    }
    return icon;
  };

  const classNames = `nui-button ${className || ''}${hasIcon ? ' has-icon' : ''}${color ? ' custom-color' : ''}${small ? ' small' : ''}`;

  return (
    <button type="button" className={classNames} onClick={onClick} disabled={disabled} {...rest}>
      {hasIcon && <Icon icon={getIcon()} width="24" height="24" style={{ marginRight: '4px' }} />}
      <span>{children}</span>
    </button>
  );
})`
  display: inline-block;
  box-sizing: border-box;
  height: 30px;
  min-width: 6em;
  border: none;
  border-radius: 4px;
  text-align: center;
  font-family: ${Theme.fontFamily};
  font-size: ${Theme.fontSizeText};
  padding: 0 15px;

  background-color: ${Theme.blue};
  color: white;
  transition: background-color .10s, opacity .25s;

  :not([disabled]):hover {
    cursor: pointer;
    background-color: ${lighten(0.1, Theme.blue)};
  }

  :disabled {
    opacity: 0.35;
  }

  :focus {
    outline: none;
  }

  &.has-icon {
    align-items: center;
    display: inline-flex;
    padding: 2.5px 6px 2.5px 4px;
  }

  &.secondary {
    background-color: ${Theme.lightBlue};
    border: 1px solid ${Theme.blue};
    color: ${Theme.blue};

    &:hover {
      background-color: ${lighten(0.1, Theme.lightBlue)};
    }
  }

  &.danger {
    background-color: ${Theme.red};
    border-color: ${Theme.red};

	  &:hover {
		  background-color: ${lighten(0.1, Theme.red)};
	  }
  }

  &.success {
    background-color: ${Theme.green};
    border-color: ${Theme.green};

	  &:hover {
		  background-color: ${lighten(0.1, Theme.green)};
	  }
  }

  & + button {
    margin-left: .25rem;
  }

  &.small {
    font-size: ${Theme.fontSizeSmallButton};
    height: 24px;
  }

  ${props => additionalColor(props.color)}
`;

const additionalColor = (color) => {
  if (color) {
    return `
      &.custom-color {
        background-color: ${color};
        border: 1px solid ${color};

        &:hover {
          background-color: ${lighten(0.1, color)};
        }
      }
    `;
  }
};

/**
 * The NekoButton works out of the box without specific attributes.
 */
const NekoButton = (props) => {
  return (<StyledButton {...props} />);
};

NekoButton.propTypes = {
  className: PropTypes.oneOf(['primary', 'secondary', 'danger', 'success']),
  disabled: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.instanceOf(IconifyIcon), PropTypes.oneOf(['setting', 'edit', 'trash'])]),
  color: PropTypes.string,
  onClick: PropTypes.func.isRequired
};

NekoButton.defaultProps = {
  className: 'primary',
  disabled: false,
  icon: null,
  color: null,
  onClick: () => {}
};

export { NekoButton };
